import { Input } from '../../components/input'
import './styles.scss'
import type { UserType } from '../../utils/types'
import { Check, EyeClosed, EyeIcon, TriangleAlert } from 'lucide-react'
import { Checkbox } from '../../components/checkbox'
import { Button } from '../../components/button'
import { useUser } from '../../hooks/setup/useUser'
import { useUserForm } from '../../hooks/setup/useUserForm'
import { usePassword } from '../../hooks/usePassword'
import { useEffect, useRef, useState } from 'react'
import { useTaxID } from '../../hooks/useTaxID'
import { useSteps } from '../../hooks/setup/useSteps'

export const User = () => {
  const { nextStep } = useSteps()
  const { register: registerUser, isLoading, getData: getDataUser } = useUser()
  const {
    register,
    handleSubmit,
    errors,
    clearErrors,
    setValue,
    getValues,
    watch,
  } = useUserForm()
  const { validatePassword } = usePassword()
  const { formatCPF } = useTaxID()
  const validation = useRef({
    lengthValid: false,
    hasNumber: false,
    hasSpecialChar: false,
    isValid: false,
  })
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (!watch('password')) return

    const valid = validatePassword(watch('password'))
    validation.current = valid
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('password')])

  useEffect(() => {
    const loadData = async () => {
      const userData = await getDataUser()

      if (!userData) return

      Object.entries(userData).forEach(([key, value]) => {
        setValue(key as keyof UserType, value)
      })
    }

    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCleanError = (type: keyof UserType) => clearErrors(type)

  const renderIcon = (isValid: boolean) => {
    if (!getValues('password')) return <Check size={15} />

    return isValid ? <Check size={15} /> : <TriangleAlert size={15} />
  }

  const renderClassName = (isValid: boolean) => {
    if (!getValues('password')) return ''

    return isValid ? 'compliance' : 'error'
  }

  const handlerRegisterUser = async (data: UserType) => {
    registerUser(data).then(async () => await nextStep())
  }

  return (
    <div className="container-initial-data-root">
      <div className="content-title">
        <strong>Vamos começar!</strong>
        <p>
          Para iniciar a configuração da sua conta, precisamos de algumas
          informações básicas:
        </p>
      </div>

      <div className="container-enter-data">
        <Input
          {...register('cpf')}
          placeholder="CPF"
          error={errors['cpf']?.message}
          fullscreen
          maxLength={14}
          onChange={(event) => setValue('cpf', formatCPF(event.target.value))}
          onInput={() => handleCleanError('cpf')}
        />

        <Input
          {...register('fullName')}
          placeholder="Nome Completo"
          error={errors['fullName']?.message}
          fullscreen
          onChange={(event) => setValue('fullName', event.target.value)}
          onInput={() => handleCleanError('fullName')}
        />

        <Input
          {...register('email')}
          placeholder="E-mail"
          fullscreen
          error={errors['email']?.message}
          onChange={(event) => setValue('email', event.target.value)}
          onInput={() => handleCleanError('email')}
        />

        <Input
          {...register('password')}
          placeholder="Senha"
          type={showPassword ? 'text' : 'password'}
          rightIcon={{
            icon: showPassword ? EyeIcon : EyeClosed,
            onClick: () => setShowPassword(!showPassword),
          }}
          fullscreen
          error={
            errors['password']?.message || errors['confirmPassword']?.message
          }
          onChange={(event) => setValue('password', event.target.value)}
          onInput={() => handleCleanError('password')}
        />

        <Input
          {...register('confirmPassword')}
          placeholder="Confirme sua Senha"
          type={showPassword ? 'text' : 'password'}
          fullscreen
          error={errors['confirmPassword']?.message}
          onChange={(event) => setValue('confirmPassword', event.target.value)}
          onInput={() => handleCleanError('confirmPassword')}
        />

        <div className="info-compliance-password">
          <span>Sua senha deve possuir:</span>

          <ul>
            <li className={renderClassName(validation.current.lengthValid)}>
              {renderIcon(validation.current.lengthValid)}
              <span>Pelo menos 8 caracteres</span>
            </li>
            <li className={renderClassName(validation.current.hasNumber)}>
              {renderIcon(validation.current.hasNumber)}
              <span>Pelo menos 1 número</span>
            </li>
            <li className={renderClassName(validation.current.hasSpecialChar)}>
              {renderIcon(validation.current.hasSpecialChar)}
              <span>Pelo menos 1 caractere especial. (!@#$%^&*)</span>
            </li>
          </ul>
        </div>

        <div className="container-accept-terms">
          <div className="content-accept-terms">
            <Checkbox width={24} height={24} {...register('acceptTerm')} />
            <span>
              Aceito os{' '}
              <a
                className="link-support"
                href={import.meta.env.VITE_URL_TERMS_LINK}
                target="_blank"
                rel="noopener noreferrer"
              >
                Termos
              </a>
            </span>
          </div>

          {errors.acceptTerm && (
            <span className="error">{errors.acceptTerm.message}</span>
          )}
        </div>

        <Button
          onClick={handleSubmit(handlerRegisterUser)}
          type="button"
          loading={isLoading}
          disabled={isLoading}
          className="content-btn-submit"
        >
          Avançar
        </Button>
      </div>
    </div>
  )
}
